import { graphqlRequest } from "@/graphql/client";
import { stripPhotoThumbnailsFromRecords } from "@/pages/PropertyDetail/utils/propertyPhotoRecords";
import { makeAutoObservable, runInAction } from "mobx";
import { batchGetProperties } from "../graphql/queries";
import { extractBodyString, ACTIVATION_STATE, fetchPropertiesFromApi } from "./propertyCore/propertyApi";
import {
  cachedPropertyIdMatchesKey,
  getCacheAge,
  isCacheFresh,
  mergePropertyPatch,
  propertyKey,
} from "./propertyCore/propertyCacheUtils";

/**
 * PropertyDetailsStore
 *
 * App-facing details domain store.
 * Internally composes a private property core for fetch/cache operations.
 */
class PropertyDetailsStore {
  propertyByKey = new Map();
  propertyLoadingByKey = new Map();
  propertyRefreshingByKey = new Map();
  propertyErrorByKey = new Map();
  propertyFetchedAtByKey = new Map();

  defaultPropertyRevalidateAfterMs = 5 * 60 * 1000;

  _inFlightPropertyKeys = new Set();
  _inFlightPropertyPromises = new Map();
  _resetGeneration = 0;
  _propertyListStore = null;

  constructor(propertyListStore) {
    this._propertyListStore = propertyListStore;
    makeAutoObservable(this, { _propertyListStore: false }, { autoBind: true });
  }

  loadProperty(params) {
    return this._loadProperty(params);
  }

  isPropertyLoading(customer, propertyId) {
    const key = propertyKey(customer, propertyId);
    return Boolean(this.propertyLoadingByKey.get(key));
  }

  getProperty(customer, propertyId) {
    const key = propertyKey(customer, propertyId);
    return this.propertyByKey.get(key) || null;
  }

  getPropertyError(customer, propertyId) {
    const key = propertyKey(customer, propertyId);
    return this.propertyErrorByKey.get(key) || null;
  }

  updatePropertyInCache(customer, propertyId, partialUpdate) {
    this.applyPropertyPatchEverywhere(customer, propertyId, partialUpdate);
  }

  applyPropertyPatchEverywhere(customer, propertyId, partialUpdate) {
    if (!customer || !propertyId || !partialUpdate || typeof partialUpdate !== "object") return;

    const key = propertyKey(customer, propertyId);
    const existing = this.propertyByKey.get(key) || null;
    const listPatchResult = this._propertyListStore?.applyPropertyPatchForCustomer?.(
      customer,
      propertyId,
      partialUpdate
    ) || { patchedProperty: null };
    const seededBase =
      existing ||
      listPatchResult.patchedProperty ||
      this._seedPropertyFromList(customer, propertyId) ||
      { id: String(propertyId), customer };
    const merged = mergePropertyPatch(seededBase, partialUpdate);

    runInAction(() => {
      this.propertyByKey.set(key, merged);
      this.propertyFetchedAtByKey.set(key, Date.now());
    });
  }

  /**
   * Marks the property inactive in details and list caches so Availability filters can still find it.
   * The current property URL keeps enough state to render the unavailable screen.
   *
   * @param {string} customer
   * @param {string} propertyId
   */
  markPropertyDeactivating(customer, propertyId) {
    if (!customer || !propertyId) return;

    this.applyPropertyPatchEverywhere(customer, propertyId, {
      activation_state: ACTIVATION_STATE.DEACTIVATING,
    });
  }

  markPropertyDeactivated(customer, propertyId) {
    if (!customer || !propertyId) return;

    this.applyPropertyPatchEverywhere(customer, propertyId, {
      active: false,
      activation_state: ACTIVATION_STATE.DEACTIVATED,
    });
  }

  /**
   * Restores an active property into details + the live list after reactivation.
   * List page caches are invalidated so a later list load cannot revive a stale
   * post-deactivation snapshot.
   *
   * @param {string} customer
   * @param {string} propertyId
   * @param {Record<string, unknown>} [extraPatch]
   */
  markPropertyReactivated(customer, propertyId, extraPatch = {}) {
    if (!customer || !propertyId) return;

    this.applyPropertyPatchEverywhere(customer, propertyId, {
      active: true,
      activation_state: ACTIVATION_STATE.ACTIVE,
      ...extraPatch,
    });

    const restored = this.getProperty(customer, propertyId);
    if (restored) {
      this._propertyListStore?.prependToList?.([restored]);
    }
    this._propertyListStore?.invalidateListCache?.({ customer });
  }

  reset() {
    this._clearAll();
  }

  resetForNewCustomer() {
    this._clearAll();
  }

  _clearAll() {
    this._resetGeneration += 1;
    this.propertyByKey.clear();
    this.propertyLoadingByKey.clear();
    this.propertyRefreshingByKey.clear();
    this.propertyErrorByKey.clear();
    this.propertyFetchedAtByKey.clear();
    this._inFlightPropertyKeys.clear();
    this._inFlightPropertyPromises.clear();
  }

  _seedPropertyFromList(customer, propertyId) {
    const list = this._propertyListStore?.listProperties;
    if (!customer || !propertyId || !Array.isArray(list)) return null;

    const fromList = list.find((p) => String(p?.id) === String(propertyId)) || null;
    if (!fromList) return null;

    const key = propertyKey(customer, propertyId);
    this.propertyByKey.set(key, fromList);
    this.propertyFetchedAtByKey.set(key, Date.now());
    return fromList;
  }

  _preserveOptimisticFields(cached, fromApi) {
    const hasValue = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim().length > 0;
      return true;
    };

    const preserveTopLevelFields = [
      "qma_report",
      "qma_s3_key",
      "qma_pdf_s3_key",
      "qma_report_url",
      "sow_s3_key",
      "hidden_address",
    ];
    const preserveAdditionalFields = [
      "qma_report",
      "qma_s3_key",
      "qma_pdf_s3_key",
      "qma_report_url",
      "sow_s3_key",
      "comp_ids",
      "comps",
      "comps_analysis",
      "estimated_price_low",
      "estimated_price_high",
      "estimated_price_avg",
    ];

    const mergedProperty = { ...fromApi };
    preserveTopLevelFields.forEach((field) => {
      if (!hasValue(mergedProperty[field]) && hasValue(cached?.[field])) {
        mergedProperty[field] = cached[field];
      }
    });

    if (
      cached?.additional_attributes &&
      typeof cached.additional_attributes === "object" &&
      mergedProperty?.additional_attributes &&
      typeof mergedProperty.additional_attributes === "object"
    ) {
      const mergedAdditionalAttributes = { ...mergedProperty.additional_attributes };
      preserveAdditionalFields.forEach((field) => {
        if (
          !hasValue(mergedAdditionalAttributes[field]) &&
          hasValue(cached.additional_attributes[field])
        ) {
          mergedAdditionalAttributes[field] = cached.additional_attributes[field];
        }
      });
      mergedProperty.additional_attributes = mergedAdditionalAttributes;
    }

    return mergedProperty;
  }

  _bustCacheForKey(key) {
    this.propertyByKey.delete(key);
    this.propertyFetchedAtByKey.delete(key);
    this.propertyErrorByKey.delete(key);
  }

  _toPropertyLookupId(propertyId) {
    const normalizedId = String(propertyId ?? "").trim();
    if (!normalizedId) return "";
    return normalizedId.replace(/^property#/i, "").trim();
  }

  _extractBatchProperty(body, requestedPropertyId, lookupPropertyId) {
    if (typeof body !== "string") return null;

    let bodyString;
    try {
      bodyString = extractBodyString(body);
    } catch {
      return null;
    }

    let parsed;
    try {
      parsed = JSON.parse(bodyString || "{}");
    } catch {
      return null;
    }
    const properties = parsed?.properties;
    if (!properties || typeof properties !== "object") return null;

    if (properties[requestedPropertyId]) return properties[requestedPropertyId];
    if (properties[lookupPropertyId]) return properties[lookupPropertyId];

    const entries = Object.values(properties);
    const matchingById =
      entries.find((item) => cachedPropertyIdMatchesKey(item?.id, requestedPropertyId)) || null;
    return matchingById;
  }

  _normalizeFetchedProperty(rawProperty) {
    if (!rawProperty || typeof rawProperty !== "object") return null;
    const nextProperty = { ...rawProperty };

    if (typeof nextProperty.additional_attributes === "string") {
      try {
        const parsedAdditionalAttributes = JSON.parse(nextProperty.additional_attributes);
        if (parsedAdditionalAttributes && typeof parsedAdditionalAttributes === "object") {
          nextProperty.additional_attributes = parsedAdditionalAttributes;
        }
      } catch {
        // Keep original string value if backend returns non-JSON content.
      }
    }

    if (Array.isArray(nextProperty.additional_attributes?.photos)) {
      nextProperty.additional_attributes = {
        ...nextProperty.additional_attributes,
        photos: stripPhotoThumbnailsFromRecords(nextProperty.additional_attributes.photos),
      };
    }

    return nextProperty;
  }

  async _fetchPropertyWithFallback({
    customer,
    requestedPropertyId,
    lookupPropertyId,
    existingCached,
    generationAtStart,
  }) {
    const listResult = await fetchPropertiesFromApi({
      customer,
      propertyIds: [lookupPropertyId],
      limit: 1,
    });

    if (this._resetGeneration !== generationAtStart) return null;

    const firstListProperty =
      listResult.isEmpty || !Array.isArray(listResult.data) || listResult.data.length === 0
        ? null
        : this._normalizeFetchedProperty(listResult.data[0]);

    if (cachedPropertyIdMatchesKey(firstListProperty?.id, requestedPropertyId)) {
      return this._preserveOptimisticFields(existingCached, firstListProperty);
    }

    const shouldUseBatchFallback = String(requestedPropertyId).startsWith("property#");
    if (!shouldUseBatchFallback) {
      if (firstListProperty) {
        return this._preserveOptimisticFields(existingCached, firstListProperty);
      }
      throw new Error("Property not found");
    }

    const batchResponse = await graphqlRequest({
      query: batchGetProperties,
      variables: {
        customer_id: customer,
        property_ids: [lookupPropertyId],
      },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });
    if (this._resetGeneration !== generationAtStart) return null;

    const statusCode = Number(batchResponse?.data?.batchGetProperties?.statusCode);
    if (statusCode !== 200) {
      if (firstListProperty) {
        return this._preserveOptimisticFields(existingCached, firstListProperty);
      }
      throw new Error("Property not found");
    }

    const batchProperty = this._extractBatchProperty(
      batchResponse?.data?.batchGetProperties?.body,
      requestedPropertyId,
      lookupPropertyId
    );
    const normalizedBatchProperty = this._normalizeFetchedProperty(batchProperty);

    if (!normalizedBatchProperty) {
      if (firstListProperty) {
        return this._preserveOptimisticFields(existingCached, firstListProperty);
      }
      throw new Error("Property not found");
    }

    return this._preserveOptimisticFields(existingCached, normalizedBatchProperty);
  }

  async _loadProperty({ customer, propertyId, revalidateAfterMs = null } = {}) {
    if (!customer || !propertyId) return null;

    const key = propertyKey(customer, propertyId);
    let existingCached = this.propertyByKey.get(key) || this._seedPropertyFromList(customer, propertyId);
    const cacheAgeMs = getCacheAge(this.propertyFetchedAtByKey.get(key));

    if (existingCached && !cachedPropertyIdMatchesKey(existingCached.id, propertyId)) {
      console.log("Property cache key/id mismatch", {
        key,
        requestedPropertyId: String(propertyId),
        cachedPropertyId: String(existingCached.id ?? ""),
      });
      this._bustCacheForKey(key);
      existingCached = null;
    }

    if (this._inFlightPropertyPromises.has(key)) {
      return this._inFlightPropertyPromises.get(key);
    }

    if (
      existingCached &&
      isCacheFresh(cacheAgeMs, revalidateAfterMs, this.defaultPropertyRevalidateAfterMs)
    ) {
      return existingCached;
    }

    const generationAtStart = this._resetGeneration;
    runInAction(() => {
      if (existingCached) {
        this.propertyRefreshingByKey.set(key, true);
        this.propertyLoadingByKey.set(key, false);
      } else {
        this.propertyLoadingByKey.set(key, true);
        this.propertyRefreshingByKey.set(key, false);
      }
      this.propertyErrorByKey.set(key, null);
      this._inFlightPropertyKeys.add(key);
    });

    const loadPromise = (async () => {
      try {
        const requestedPropertyId = String(propertyId ?? "").trim();
        const lookupPropertyId = this._toPropertyLookupId(requestedPropertyId);
        if (!lookupPropertyId) {
          throw new Error("Property lookup id is invalid");
        }

        let mergedProperty = await this._fetchPropertyWithFallback({
          customer,
          requestedPropertyId,
          lookupPropertyId,
          existingCached,
          generationAtStart,
        });
        if (this._resetGeneration !== generationAtStart || !mergedProperty) return null;

        if (!cachedPropertyIdMatchesKey(mergedProperty.id, propertyId)) {
          console.log("Property fetch key/id mismatch on first attempt", {
            key,
            requestedPropertyId: String(propertyId),
            fetchedPropertyId: String(mergedProperty.id ?? ""),
          });
          mergedProperty = await this._fetchPropertyWithFallback({
            customer,
            requestedPropertyId,
            lookupPropertyId,
            existingCached,
            generationAtStart,
          });
          if (this._resetGeneration !== generationAtStart || !mergedProperty) return null;
          if (!cachedPropertyIdMatchesKey(mergedProperty.id, propertyId)) {
            console.log("Property fetch key/id mismatch on second attempt", {
              key,
              requestedPropertyId: String(propertyId),
              fetchedPropertyId: String(mergedProperty.id ?? ""),
            });
            throw new Error("Property ID mismatch after retry");
          }
        }

        runInAction(() => {
          this.propertyByKey.set(key, mergedProperty);
          this.propertyFetchedAtByKey.set(key, Date.now());
        });
        return mergedProperty;
      } catch {
        if (this._resetGeneration !== generationAtStart) return null;
        runInAction(() => {
          this.propertyErrorByKey.set(key, "Failed to load property");
        });
        return null;
      } finally {
        if (this._resetGeneration === generationAtStart) {
          runInAction(() => {
            this.propertyLoadingByKey.set(key, false);
            this.propertyRefreshingByKey.set(key, false);
            this._inFlightPropertyKeys.delete(key);
          });
        }
        this._inFlightPropertyPromises.delete(key);
      }
    })();

    this._inFlightPropertyPromises.set(key, loadPromise);
    return loadPromise;
  }
}

export default PropertyDetailsStore;
